import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { RichTextEditor } from "../../components/RichTextEditor";
import { useCreateNewsfeed, useUpdateNewsfeed, type Newsfeed } from "./api";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { timeZoneAbbreviation } from "../../lib/format";

interface Props {
  open: boolean;
  existing: Newsfeed | null;
  onClose: () => void;
}

function splitPublishDate(value: string | undefined) {
  if (!value) return { date: "", time: "" };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: "", time: "" };
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  };
}

export function NewsfeedDialog({ open, existing, onClose }: Props) {
  const { principal } = useAuth();
  const notify = useNotify();
  const create = useCreateNewsfeed();
  const update = useUpdateNewsfeed();

  const initial = splitPublishDate(existing?.publish_date);
  const [title, setTitle] = useState(existing?.title ?? "");
  const [text, setText] = useState(existing?.text ?? "");
  const [link, setLink] = useState(existing?.link ?? "");
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [showTitle, setShowTitle] = useState(existing?.show_title ?? true);

  // Link is optional -- plenty of announcements are just words.
  const missing: string[] = [];
  if (!title) missing.push("a title");
  if (!date) missing.push("a publish date");
  if (!time) missing.push("a publish time");
  const incomplete = missing.length > 0;
  const busy = create.isPending || update.isPending;

  async function submit() {
    const payload = {
      title,
      text,
      link: link.trim(),
      publish_date: new Date(`${date}T${time}`).toISOString(),
      created_by: principal?.userId ?? "",
      show_title: showTitle,
    };
    try {
      if (existing) {
        await update.mutateAsync({ ...payload, id: existing.id });
        notify.success("Post updated");
      } else {
        await create.mutateAsync(payload);
        notify.success("Post created");
      }
      onClose();
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not save the post.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{existing ? "Edit post" : "New newsfeed post"}</DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
          margin="normal"
        />
        <RichTextEditor label="Text" value={text} onChange={setText} />
        <TextField
          label="Link"
          value={link}
          onChange={(event) => setLink(event.target.value)}
          fullWidth
          margin="normal"
          helperText="Optional. Where the post sends people if they tap it."
        />
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <TextField
            label="Publish date"
            required
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            label="Publish time"
            required
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={showTitle}
              onChange={(event) => setShowTitle(event.target.checked)}
            />
          }
          label="Show title"
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {incomplete
            ? `Still needs ${missing.join(", ")}.`
            : `Times are ${timeZoneAbbreviation()}.`}
        </Typography>
        <Box>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy || incomplete}>
            {existing ? "Save changes" : "Create post"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
